import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ChatProvider } from './context/ChatContext';
import { AuthProvider } from './context/AuthContext';
import MainLayout from './components/MainLayout';
import AuthGuard from './components/AuthGuard';
import AuthCallback from './components/AuthCallback';
import './styles/custom.css';
import './App.css';

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: any }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  componentDidCatch(error: any, errorInfo: any) {
    // You can log error info here if needed
    // console.error('ErrorBoundary caught:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return <div style={{ color: 'red', padding: 32 }}><h2>Something went wrong.</h2><pre>{String(this.state.error)}</pre></div>;
    }
    return this.props.children;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#308E75',
    },
    secondary: {
      main: '#62D3B0',
    },
    background: {
      default: '#FFFFFF',
    },
    text: {
      primary: '#1D1E1E',
      secondary: '#666666',
    },
    error: {
      main: '#E1502F',
    },
    warning: {
      main: '#FFCF08',
    },
    success: {
      main: '#80A405',
    },
  },
  typography: {
    fontFamily: 'Montserrat, "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
    h1: { fontSize: '20px', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: '18px', fontWeight: 700, lineHeight: 1.2 },
    h3: { fontSize: '16px', fontWeight: 700, lineHeight: 1.2 },
    h4: { fontSize: '14px', fontWeight: 700, lineHeight: 1.2 },
    h5: { fontSize: '12px', fontWeight: 700, lineHeight: 1.2 },
    h6: { fontSize: '11px', fontWeight: 700, lineHeight: 1.2 },
    body1: { fontSize: '14px', fontWeight: 400, lineHeight: 1.4 },
    body2: { fontSize: '12px', fontWeight: 400, lineHeight: 1.4 },
    subtitle1: { fontSize: '12px', fontWeight: 400 },
    subtitle2: { fontSize: '11px', fontWeight: 400 },
  },
  shape: {
    borderRadius: 4,
  },
  spacing: 8,
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.1)',
    '0 3px 6px rgba(0,0,0,0.15)',
    'none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none','none'
  ],
});

function App() {
  // console.log('App rendered');
  
  // Check if this is the auth callback route
  if (window.location.pathname === '/auth/callback') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <AuthCallback />
        </AuthProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AuthGuard>
          <ChatProvider>
            <ErrorBoundary>
              <MainLayout />
            </ErrorBoundary>
          </ChatProvider>
        </AuthGuard>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
